/*
 * ToneWoodAmp site header.
 *
 * Owns the drawer menu and keeps the cart count/total current after an
 * in-page add-to-cart, so the header does not go stale until the next
 * navigation.
 */

class TwaHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.menu = this.querySelector('[data-menu]');

    if (this.toggle && this.menu) {
      this.toggle.addEventListener('click', () => this.setOpen(this.menu.hidden));
    }

    // Clicking outside or pressing Escape closes the drawer. Capture phase so
    // this still runs if something inside stops propagation.
    this.onDocClick = (event) => {
      if (!this.menu || this.menu.hidden) return;
      if (this.contains(event.target)) return;
      this.setOpen(false);
    };
    this.onKey = (event) => {
      if (event.key === 'Escape' && this.menu && !this.menu.hidden) {
        this.setOpen(false);
        this.toggle?.focus();
      }
    };
    document.addEventListener('click', this.onDocClick, true);
    document.addEventListener('keydown', this.onKey);

    // Any part of the theme can announce a cart change; the bundle
    // configurator fires this after its /cart/add.js call succeeds.
    this.onCartUpdate = () => this.refreshCart();
    document.addEventListener('twa:cart-updated', this.onCartUpdate);
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
      if (Number.isFinite(cartData?.item_count) && Number.isFinite(cartData?.total_price)) {
        // The cart page already has the updated totals; show them immediately.
        this.cartRefreshVersion = (this.cartRefreshVersion || 0) + 1;
        this.renderCart(cartData);
      } else {
        this.refreshCart();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick, true);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('twa:cart-updated', this.onCartUpdate);
    this.cartUpdateUnsubscriber?.();
    this.cartRefreshVersion = (this.cartRefreshVersion || 0) + 1;
  }

  setOpen(open) {
    if (!this.menu || !this.toggle) return;
    this.menu.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
  }

  async refreshCart() {
    const version = this.cartRefreshVersion = (this.cartRefreshVersion || 0) + 1;
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      if (version === this.cartRefreshVersion) this.renderCart(cart);
    } catch (error) {
      // A stale header is not worth breaking the page over — it corrects
      // itself on the next navigation.
      console.warn('[twa-header] cart refresh failed:', error);
    }
  }

  renderCart(cart) {
    const count = this.querySelector('[data-cart-count]');
    const total = this.querySelector('[data-cart-total]');
    const savings = this.querySelector('[data-cart-savings]');

    if (count) count.textContent = cart.item_count;
    if (total) total.textContent = this.money(cart.total_price);
    if (savings) {
      savings.textContent = `You saved ${this.money(cart.total_discount || 0)}`;
      savings.hidden = !cart.total_discount;
    }
  }

  /* cart.js returns cents; the shared formatter applies the shop's currency. */
  money(cents) {
    return window.twaMoney(cents, this.dataset.moneyFormat);
  }
}

customElements.define('twa-header', TwaHeader);
